import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Modal from '../components/Modal';
import PeoplePicker from '../components/PeoplePicker';
import { useConfirm } from '../components/ConfirmDialog';
import { downloadCsvNamed, downloadXlsxBook } from '../lib/exportTable';
import { hours } from '../lib/hours';
import { money } from '../lib/money';
import { fmtDate, fmtDateTime } from '../lib/when';
import '../components/Assets.css';
import './MonthlyReport.css';

/**
 * THE MONTHLY REPORT (118; wave 3, batch 2) - Ticket Mgmt -> Reporting.
 *
 * One month, or any range of days, of how IT went: tickets, the SLA in
 * working hours, contractor hours, the asset register, joiners and leavers.
 * Every figure comes from the server (GET /reports/monthly), which works it
 * out with the same functions the Dashboard, the SLA board and the register
 * use - nothing is counted here, so nothing here can disagree with them.
 *
 * A month that has been emailed is shown AS IT WAS SENT (the stored
 * snapshot), with "Show today's figures" beside it; the email itself - who
 * gets it, on or off, a preview, Send now - is behind "Monthly email".
 *
 * Out of the page: Print / PDF (the browser's own print, with a print
 * stylesheet that leaves only the report), Excel (a workbook with a sheet per
 * table, lib/exportTable.js) and CSV (every figure as one long table).
 *
 * The address carries the choice - ?month=2026-10, or ?from=...&to=... - so
 * the email's link opens the month it is about and the back button works.
 */
const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const errorOf = (e, fallback) => e?.response?.data?.error || fallback;

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const pad = (n) => String(n).padStart(2, '0');
const addMonths = (month, n) => {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`;
};
const thisMonth = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`; };
const monthWords = (month) => new Date(`${month}-01T12:00:00Z`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const dayWords = (day) => (day ? new Date(`${day}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }) : '');

const num = (v) => (v === null || v === undefined ? '–' : Number(v).toLocaleString('en-GB'));
const pct = (v) => (v === null || v === undefined ? '–' : `${v}%`);
const hrs = (v) => (v === null || v === undefined ? '–' : hours(v));
const show = (x) => (x.unit === 'pct' ? pct(x.value) : x.unit === 'hours' ? hrs(x.value) : num(x.value));

/* ---------------------------------------------------------------- pieces */

function Table({ columns, rows, empty, label, testId }) {
  if (!rows || rows.length === 0) return <p className="mr-empty">{empty}</p>;
  return (
    <div className="mr-table-wrap" role="region" aria-label={label} tabIndex={0}>
      <table className="mr-table" data-testid={testId}>
        <thead>
          <tr>{columns.map((c) => <th key={c.key} scope="col" className={c.num ? 'is-num' : undefined}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || r.key || i}>
              {columns.map((c, ci) => {
                const v = c.render ? c.render(r) : r[c.key];
                return ci === 0
                  ? <th key={c.key} scope="row">{v}</th>
                  : <td key={c.key} className={c.num ? 'is-num' : undefined}>{v}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A breakdown: name, count, a bar of its share. */
function Bars({ rows, label, testId }) {
  const total = rows.reduce((s, r) => s + r.count, 0);
  const max = rows.reduce((m, r) => Math.max(m, r.count), 0);
  if (!rows.length) return <p className="mr-empty">None.</p>;
  return (
    <ul className="mr-bars" aria-label={label} data-testid={testId}>
      {rows.map((r) => (
        <li key={r.name} className="mr-bar">
          <span className="mr-bar-name">{r.name}</span>
          <span className="mr-bar-track" aria-hidden="true">
            <span className="mr-bar-fill" style={{ width: `${max > 0 ? (r.count / max) * 100 : 0}%` }} />
          </span>
          <span className="mr-bar-count">{r.count}<span className="mr-bar-share">{total > 0 ? ` · ${Math.round((r.count / total) * 100)}%` : ''}</span></span>
        </li>
      ))}
    </ul>
  );
}

/** Raised and resolved, the period and the three before it, as columns. */
function Trend({ trend }) {
  const max = Math.max(1, ...trend.flatMap((t) => [t.raised, t.resolved, t.open_at_end]));
  return (
    <div className="mr-trend" role="img"
         aria-label={trend.map((t) => `${t.label}: ${t.raised} raised, ${t.resolved} resolved, ${t.open_at_end} open at the end`).join('; ')}>
      <div className="mr-trend-cols">
        {trend.map((t) => (
          <div key={t.from} className={`mr-trend-col${t.current ? ' is-current' : ''}`}>
            <div className="mr-trend-bars">
              {[['raised', t.raised], ['resolved', t.resolved], ['open', t.open_at_end]].map(([k, v]) => (
                <span key={k} className={`mr-trend-bar is-${k}`} style={{ height: `${(v / max) * 100}%` }} title={`${v}`}>
                  <span className="mr-trend-val">{v}</span>
                </span>
              ))}
            </div>
            <div className="mr-trend-label">{t.label}</div>
          </div>
        ))}
      </div>
      <div className="mr-legend" aria-hidden="true">
        <span><i className="is-raised" />Raised</span><span><i className="is-resolved" />Resolved</span><span><i className="is-open" />Open at the end</span>
      </div>
    </div>
  );
}

function Change({ c, invert = false }) {
  if (!c || c.change_pct === null || c.change_pct === undefined) return null;
  const up = c.change_pct > 0;
  const good = c.change_pct === 0 ? null : (invert ? !up : up);
  return (
    <span className="mr-change" data-tone={good === null ? 'flat' : good ? 'good' : 'bad'}
          title={`Average of the three periods before: ${c.average}`}>
      {c.change_pct === 0 ? 'same as before' : `${up ? '▲' : '▼'} ${Math.abs(c.change_pct)}% vs the 3 before`}
    </span>
  );
}

function Section({ id, title, lead, children }) {
  return (
    <section className="mr-section" id={id} aria-labelledby={`${id}-h`}>
      <h2 id={`${id}-h`}>{title}</h2>
      {lead && <p className="mr-lead">{lead}</p>}
      {children}
    </section>
  );
}

/* --------------------------------------------------------------- exports */

const HOURS_COLS = [
  { key: 'name', label: 'Name' }, { key: 'logged', label: 'Logged (h)', type: 'number' },
  { key: 'approved', label: 'Approved (h)', type: 'number' }, { key: 'awaiting', label: 'Awaiting sign-off (h)', type: 'number' },
  { key: 'billed', label: 'Billed (h)', type: 'number' }, { key: 'billed_amount', label: 'Billed (€)', type: 'money' },
  { key: 'ready', label: 'Ready to bill (h)', type: 'number' }
];

/** Every table in the pack as a sheet: [{ name, columns, rows }]. The CSV is built from the same list. */
export function sheetsOf(pack, snapshot) {
  const t = pack.tickets; const s = pack.sla; const h = pack.hours; const a = pack.assets; const pe = pack.people;
  const sheets = [
    { name: 'Summary', columns: [{ key: 'item', label: 'Figure' }, { key: 'value', label: 'Value' }],
      rows: [
        { item: 'Period', value: pack.period.label },
        { item: 'From', value: pack.period.from }, { item: 'To', value: pack.period.last },
        { item: snapshot ? 'Stored (as sent) at' : 'Worked out at', value: snapshot ? snapshot.generated_at : pack.generated_at },
        ...(pack.headline || []).map((x) => ({ item: x.label, value: x.value }))
      ] },
    { name: 'Tickets', columns: [{ key: 'by', label: 'Breakdown' }, { key: 'name', label: 'Name' }, { key: 'count', label: 'Raised', type: 'number' }],
      rows: [...t.by_category.map((r) => ({ by: 'Category', ...r })), ...t.by_priority.map((r) => ({ by: 'Priority', ...r })),
        ...t.by_department.map((r) => ({ by: 'Department', ...r }))] },
    { name: 'Trend', columns: [{ key: 'label', label: 'Period' }, { key: 'raised', label: 'Raised', type: 'number' },
      { key: 'resolved', label: 'Resolved', type: 'number' }, { key: 'open_at_end', label: 'Open at the end', type: 'number' }], rows: t.trend },
    { name: 'SLA by profile', columns: [{ key: 'name', label: 'SLA profile' }, { key: 'tickets', label: 'Tickets', type: 'number' },
      { key: 'response_met', label: 'Response met', type: 'number' }, { key: 'response_rate', label: 'Response met (%)', type: 'number' },
      { key: 'resolution_met', label: 'Resolution met', type: 'number' }, { key: 'resolution_rate', label: 'Resolution met (%)', type: 'number' },
      { key: 'breached', label: 'Breached', type: 'number' }, { key: 'median_response_hours', label: 'Median response (working h)', type: 'number' },
      { key: 'median_resolution_hours', label: 'Median resolution (working h)', type: 'number' }],
    rows: [...s.by_profile, { name: 'All', ...s }] },
    { name: 'SLA breaches', columns: [{ key: 'ticket_number', label: 'Ticket' }, { key: 'subject', label: 'Subject' }, { key: 'priority', label: 'Priority' },
      { key: 'category', label: 'Category' }, { key: 'profile', label: 'SLA profile' }, { key: 'response_time_hours', label: 'Response (h)', type: 'number' },
      { key: 'response_sla_hours', label: 'Response target (h)', type: 'number' }, { key: 'resolution_time_hours', label: 'Resolution (h)', type: 'number' },
      { key: 'resolution_sla_hours', label: 'Resolution target (h)', type: 'number' }, { key: 'created_at', label: 'Raised', type: 'date' },
      { key: 'resolved_at', label: 'Resolved', type: 'date' }], rows: s.breaches },
    { name: 'Hours by contractor', columns: HOURS_COLS, rows: [...h.by_contractor, { name: 'Total', ...h }] },
    { name: 'Hours by client', columns: HOURS_COLS, rows: [...h.by_client, { name: 'Total', ...h }] }
  ];
  if (a && a.ready) {
    sheets.push({ name: 'Devices', columns: [{ key: 'kind', label: 'List' }, { key: 'asset_tag', label: 'Tag' }, { key: 'category', label: 'Type' },
      { key: 'model', label: 'Model' }, { key: 'company', label: 'Company' }, { key: 'day', label: 'Date', type: 'date' }, { key: 'holder', label: 'Holder' }],
    rows: [...a.added.items.map((x) => ({ kind: 'Added', ...x, day: x.created_at })),
      ...a.retired.items.map((x) => ({ kind: x.status === 'disposed' ? 'Disposed' : 'Retired', ...x, day: x.retired_at })),
      ...a.warranty_90.items.map((x) => ({ kind: 'Warranty ending', ...x, day: x.warranty_end })),
      ...a.no_owner.by_category.map((x) => ({ kind: 'Nobody holds', category: x.name, asset_tag: `${x.count} device${x.count === 1 ? '' : 's'}` }))] });
    sheets.push({ name: 'Stock takes', columns: [{ key: 'place', label: 'Place' }, { key: 'finished_at', label: 'Finished', type: 'date' },
      { key: 'expected', label: 'Expected', type: 'number' }, { key: 'found', label: 'Found', type: 'number' }, { key: 'misplaced', label: 'Misplaced', type: 'number' },
      { key: 'missing', label: 'Missing', type: 'number' }, { key: 'extra', label: 'Extra', type: 'number' }], rows: a.stocktakes });
  }
  if (pe && pe.ready) {
    sheets.push({ name: 'People', columns: [{ key: 'kind', label: 'Joiner or leaver' }, { key: 'name', label: 'Name' }, { key: 'day', label: 'Day', type: 'date' },
      { key: 'status', label: 'Checklist' }, { key: 'steps', label: 'Steps' }],
    rows: [...pe.joiners.map((x) => ({ kind: 'Joiner', ...x, steps: `${x.steps_done} of ${x.steps}` })),
      ...pe.leavers.map((x) => ({ kind: 'Leaver', ...x, steps: `${x.steps_done} of ${x.steps}` }))] });
  }
  return sheets;
}

/**
 * Every figure as one long table - Section, Item, Measure, Value - for a
 * pivot table or a database. Each sheet's number columns are its measures;
 * its other columns together name the item ("Category · Hardware",
 * "JAR-2026-0001 · Docking station dead · High"). A list with no numbers (the
 * devices, the people) is one line per row, its details as the value.
 */
export function longRows(sheets) {
  const out = [];
  const text = (c, v) => (v === null || v === undefined || v === '' ? null : c.type === 'date' ? String(v).slice(0, 10) : String(v));
  for (const sh of sheets) {
    const measures = sh.columns.filter((c) => c.type === 'number' || c.type === 'money');
    const names = sh.columns.filter((c) => !measures.includes(c));
    for (const r of sh.rows) {
      if (measures.length) {
        const item = names.map((c) => text(c, r[c.key])).filter(Boolean).join(' · ');
        for (const c of measures) {
          const v = r[c.key];
          if (v === null || v === undefined || v === '' || !Number.isFinite(Number(v))) continue;
          out.push({ section: sh.name, item, measure: c.label, value: Number(v) });
        }
      } else if (names.length === 2) {
        const v = r[names[1].key];
        if (v !== null && v !== undefined && v !== '') out.push({ section: sh.name, item: text(names[0], r[names[0].key]), measure: names[1].label, value: v });
      } else {
        const [first, ...rest] = names;
        out.push({ section: sh.name, item: text(first, r[first.key]), measure: 'Details',
          value: rest.map((c) => text(c, r[c.key])).filter(Boolean).join(' · ') });
      }
    }
  }
  return out;
}

/* --------------------------------------------------------- the email box */

function EmailSettings({ onClose, shownMonth }) {
  const [confirm, confirmDialog] = useConfirm();
  const [s, setS] = useState(null);
  const [people, setPeople] = useState([]);
  const [chosen, setChosen] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [error, setError] = useState('');
  const [said, setSaid] = useState('');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    try {
      const [sch, us] = await Promise.all([
        axios.get(`${API}/reports/schedule`),
        axios.get(`${API}/users`).catch(() => ({ data: [] }))
      ]);
      setS(sch.data);
      setEnabled(Boolean(sch.data.enabled));
      setChosen((sch.data.recipients || []).map((r) => String(r.id)));
      setPeople((Array.isArray(us.data) ? us.data : [])
        .filter((u) => u.active !== false && u.email !== 'workflow@jarvis.invalid')
        .map((u) => ({ id: String(u.id), name: u.display_name || u.name || u.email, company: u.email })));
      setError('');
    } catch (e) { setError(errorOf(e, 'Could not load the email settings.')); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const month = shownMonth && s && shownMonth <= s.default_month ? shownMonth : s?.default_month;
  const dirty = s && (enabled !== Boolean(s.enabled)
    || chosen.slice().sort().join() !== (s.recipients || []).map((r) => String(r.id)).sort().join());

  const save = async () => {
    setBusy(true); setError(''); setSaid('');
    try {
      const r = await axios.put(`${API}/reports/schedule`, { enabled, recipients: chosen });
      setS(r.data); setSaid('Saved.');
    } catch (e) { setError(errorOf(e, 'Could not save.')); } finally { setBusy(false); }
  };
  const showPreview = async () => {
    setBusy(true); setError('');
    try { setPreview((await axios.post(`${API}/reports/preview`, { month })).data); } catch (e) { setError(errorOf(e, 'Could not make the preview.')); } finally { setBusy(false); }
  };
  const sendNow = async () => {
    const n = (s?.recipients || []).length;
    const ok = await confirm({
      title: `Send the ${monthWords(month)} report now?`, confirmLabel: 'Send now',
      message: `It goes to ${n} ${n === 1 ? 'person' : 'people'}. ${monthWords(month)} is stored as it is now, if it has not been already, and every later look at it shows those figures.`
    });
    if (!ok) return;
    setBusy(true); setError(''); setSaid('');
    try {
      const r = (await axios.post(`${API}/reports/send`, { month })).data;
      setSaid(r.not_sent ? `Sent to ${r.sent} of ${r.sent + r.not_sent}. ${r.results.filter((x) => !x.ok).map((x) => `${x.name}: ${x.reason}`).join('; ')}`
        : `Sent to ${r.sent} ${r.sent === 1 ? 'person' : 'people'}.`);
      load();
    } catch (e) { setError(errorOf(e, 'Could not send it.')); } finally { setBusy(false); }
  };

  const footer = s && s.ready ? (
    <div className="mr-email-actions">
      <button type="button" className="btn btn-outline" onClick={showPreview} disabled={busy}>Preview email</button>
      <button type="button" className="btn btn-outline" onClick={sendNow} disabled={busy || dirty || !(s.recipients || []).length}
              title={dirty ? 'Save first' : undefined}>Send now</button>
      <span className="mr-email-gap" />
      <button type="button" className="btn btn-primary" onClick={save} disabled={busy || !dirty}>Save</button>
    </div>
  ) : null;

  return (
    <Modal title="Monthly email" subtitle="A short summary of the report, with a link to it" onClose={onClose} footer={footer}>
      {confirmDialog}
      <div className="mr-email" data-testid="mr-email">
        {error && <p className="as-note" data-tone="warning" role="alert">{error}</p>}
        {said && <p className="as-note" data-tone="success" role="status">{said}</p>}
        {!s && !error && <p className="mr-empty">Loading…</p>}
        {s && !s.ready && (
          <p className="as-note" data-tone="info" data-testid="mr-need">{s.need?.error || 'The monthly email needs migration 118.'}</p>
        )}
        {s && s.ready && (
          <>
            {!s.mail?.configured && (
              <p className="as-note" data-tone="warning" data-testid="mr-no-mail">
                Email is not set up on this server ({s.mail?.reason}), so nothing will reach anybody until it is. Every send is still recorded.
              </p>
            )}
            <label className="mr-switch">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="mr-enabled" />
              <span>
                <strong>Email the report every month</strong>
                <span className="mr-sub">On the first working day of the month at {pad(s.send_hour)}:00 Malta time - weekends and public holidays skipped - with last month’s figures.</span>
              </span>
            </label>
            <div className="mr-field">
              {/* Inline, not a popover: a popover inside a dialog is one layer
                  too many, and on a phone the dialog is already a sheet. */}
              <PeoplePicker label="Who gets it" options={people} value={chosen} onChange={setChosen} inline idBase="mr-recipients" />
              {chosen.length > 0 && (
                <ul className="mr-chips" aria-label="Chosen">
                  {chosen.map((id) => {
                    const p = people.find((x) => x.id === id) || (s.recipients || []).map((r) => ({ id: String(r.id), name: r.name })).find((x) => x.id === id);
                    return (
                      <li key={id} className="mr-chip">
                        {p ? p.name : 'Somebody'}
                        <button type="button" aria-label={`Remove ${p ? p.name : 'them'}`} onClick={() => setChosen(chosen.filter((x) => x !== id))}>×</button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            {s.enabled && s.next && (
              <p className="mr-next" data-testid="mr-next">
                Next: <strong>{dayWords(s.next.day)}</strong> at {pad(s.send_hour)}:00, with {monthWords(s.next.month)}.
              </p>
            )}
            {!s.enabled && <p className="mr-sub">Switched off: nothing is sent by itself. Send now still works.</p>}
            {(s.recent || []).length > 0 && (
              <details className="mr-recent">
                <summary>Last {s.recent.length} sends</summary>
                <ul>
                  {s.recent.map((x) => (
                    <li key={x.id} data-ok={x.ok ? 'yes' : 'no'}>
                      <span>{monthWords(x.month)} → {x.name || x.email}</span>
                      <span className="mr-sub">{fmtDateTime(x.sent_at)} · {x.kind === 'scheduled' ? 'scheduled' : `by ${x.sent_by_name || 'hand'}`} · {x.ok ? 'sent' : `not sent: ${x.reason}`}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
            {preview && (
              <div className="mr-preview" data-testid="mr-preview">
                <p className="mr-sub">Subject: <strong>{preview.subject}</strong>{preview.from_snapshot ? ' · as stored' : ' · today’s figures'}</p>
                <iframe title="The email" sandbox="" srcDoc={preview.html} className="mr-preview-frame" />
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

/* --------------------------------------------------------------- the page */

export default function MonthlyReport() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const qMonth = params.get('month');
  const qFrom = params.get('from');
  const qTo = params.get('to');
  const live = params.get('live') === '1';
  const ranged = DAY_RE.test(qFrom || '') && DAY_RE.test(qTo || '');
  const month = !ranged && MONTH_RE.test(qMonth || '') ? qMonth : null;

  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(ranged ? 'range' : 'month');
  const [from, setFrom] = useState(qFrom || '');
  const [to, setTo] = useState(qTo || '');
  const [email, setEmail] = useState(location.hash === '#email');

  const go = useCallback((q) => {
    const sp = new URLSearchParams(q);
    navigate({ pathname: location.pathname, search: sp.toString() ? `?${sp}` : '' });
  }, [navigate, location.pathname]);

  useEffect(() => {
    let gone = false;
    setLoading(true); setError('');
    const query = ranged ? { from: qFrom, to: qTo } : { ...(month ? { month } : {}), ...(live ? { live: '1' } : {}) };
    axios.get(`${API}/reports/monthly`, { params: query })
      .then((r) => { if (!gone) setData(r.data); })
      .catch((e) => { if (!gone) { setData(null); setError(errorOf(e, 'Could not build the report.')); } })
      .finally(() => { if (!gone) setLoading(false); });
    return () => { gone = true; };
  }, [ranged, qFrom, qTo, month, live]);

  const pack = data?.pack;
  const snapshot = data?.snapshot;
  const shownMonth = pack?.period?.month || month;
  const current = thisMonth();

  const sheets = useMemo(() => (pack ? sheetsOf(pack, data.live ? null : snapshot) : []), [pack, data, snapshot]);
  const fileBase = pack ? `jarvis-report-${pack.period.kind === 'month' ? pack.period.month : `${pack.period.from}-to-${pack.period.last}`}` : 'jarvis-report';
  const toExcel = () => downloadXlsxBook(sheets, fileBase, `${fileBase}.xlsx`);
  const toCsv = () => downloadCsvNamed([{ key: 'section', label: 'Section' }, { key: 'item', label: 'Item' },
    { key: 'measure', label: 'Measure' }, { key: 'value', label: 'Value' }], longRows(sheets), `${fileBase}.csv`);

  const showRange = (e) => {
    e.preventDefault();
    if (DAY_RE.test(from) && DAY_RE.test(to)) go({ from, to });
  };

  const t = pack?.tickets; const s = pack?.sla; const h = pack?.hours; const a = pack?.assets; const pe = pack?.people;

  return (
    <div className="container mr-page" data-testid="mr-page">
      <div className="page-bar mr-bar">
        <div className="page-bar-title">
          <h1 className="page-title">Monthly report</h1>
        </div>
        <div className="toolbar mr-no-print">
          <button type="button" className="btn btn-outline" onClick={() => setEmail(true)} data-testid="mr-email-open">Monthly email</button>
        </div>
      </div>

      <div className="mr-controls mr-no-print" role="group" aria-label="Which period">
        <div className="mr-seg" role="tablist" aria-label="Month or range">
          <button type="button" role="tab" aria-selected={mode === 'month'} className={mode === 'month' ? 'is-on' : ''}
                  onClick={() => { setMode('month'); if (ranged) go({ month: shownMonth || addMonths(current, -1) }); }}>Month</button>
          <button type="button" role="tab" aria-selected={mode === 'range'} className={mode === 'range' ? 'is-on' : ''}
                  onClick={() => { setMode('range'); if (!from && pack) { setFrom(pack.period.from); setTo(pack.period.last); } }}>Custom range</button>
        </div>
        {mode === 'month' ? (
          <div className="mr-month">
            <button type="button" className="btn btn-outline mr-step" aria-label="The month before"
                    onClick={() => go({ month: addMonths(shownMonth || addMonths(current, -1), -1) })}>‹</button>
            <input type="month" aria-label="Month" value={shownMonth || ''} max={current} data-testid="mr-month"
                   onChange={(e) => { if (MONTH_RE.test(e.target.value)) go({ month: e.target.value }); }} />
            <button type="button" className="btn btn-outline mr-step" aria-label="The month after"
                    disabled={!shownMonth || shownMonth >= current}
                    onClick={() => go({ month: addMonths(shownMonth, 1) })}>›</button>
          </div>
        ) : (
          <form className="mr-range" onSubmit={showRange}>
            <label>From <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="mr-from" required /></label>
            <label>To <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} data-testid="mr-to" required /></label>
            <button type="submit" className="btn btn-secondary">Show</button>
          </form>
        )}
        <div className="mr-exports">
          <button type="button" className="btn btn-outline" onClick={() => window.print()} disabled={!pack} data-testid="mr-print">Print / PDF</button>
          <button type="button" className="btn btn-outline" onClick={toExcel} disabled={!pack} data-testid="mr-excel">Excel</button>
          <button type="button" className="btn btn-outline" onClick={toCsv} disabled={!pack} data-testid="mr-csv">CSV</button>
        </div>
      </div>

      {error && <p className="as-note" data-tone="warning" role="alert">{error}</p>}
      {loading && !pack && <p className="mr-empty" aria-live="polite">Working out the figures…</p>}

      {pack && (
        <article className={`mr-report${loading ? ' is-loading' : ''}`} aria-busy={loading} data-testid="mr-report">
          <header className="mr-head">
            <p className="mr-kicker">NCMB · Jarvis {pack.period.kind === 'month' ? 'monthly report' : 'report'}</p>
            <h2 className="mr-title" data-testid="mr-title">{pack.period.label}</h2>
            <p className="mr-meta">
              {fmtDate(`${pack.period.from}T12:00:00Z`)} – {fmtDate(`${pack.period.last}T12:00:00Z`)} · Malta time
              {!pack.complete && <span className="mr-badge" data-tone="info">Not finished yet</span>}
            </p>
            {snapshot && !data.live && (
              <p className="mr-status mr-no-print-soft" data-tone="stored" data-testid="mr-stored">
                As sent: stored {fmtDateTime(snapshot.generated_at)}{snapshot.source === 'schedule' ? ' by the monthly email' : snapshot.generated_by_name ? ` by ${snapshot.generated_by_name}` : ''}
                {' '}· emailed to {snapshot.sent} {snapshot.sent === 1 ? 'person' : 'people'}.
                <button type="button" className="btn btn-plain btn-sm mr-no-print" onClick={() => go({ month: shownMonth, live: '1' })} data-testid="mr-live">Show today’s figures</button>
              </p>
            )}
            {snapshot && data.live && (
              <p className="mr-status" data-tone="live" data-testid="mr-is-live">
                Today’s figures. This month was sent on {fmtDate(snapshot.generated_at)}, and may since have changed.
                <button type="button" className="btn btn-plain btn-sm mr-no-print" onClick={() => go({ month: shownMonth })}>Show as sent</button>
              </p>
            )}
            {!snapshot && <p className="mr-status" data-tone="live">Worked out {fmtDateTime(pack.generated_at)} from today’s data.</p>}
          </header>

          <ul className="mr-strip" aria-label="Key figures" data-testid="mr-strip">
            {(pack.headline || []).map((x) => (
              <li key={x.key} className="mr-tile" data-key={x.key}>
                <span className="mr-tile-label">{x.label}</span>
                <strong className="mr-tile-value">{show(x)}</strong>
              </li>
            ))}
          </ul>

          <Section id="mr-tickets" title="Tickets" lead="Raised in the period, by when they were raised; resolved by when they were resolved; open at the end of the last day.">
            <div className="mr-kpis">
              <div className="mr-kpi"><span>Raised</span><strong>{num(t.raised)}</strong><Change c={t.vs_previous?.raised} invert /></div>
              <div className="mr-kpi"><span>Resolved</span><strong>{num(t.resolved)}</strong><Change c={t.vs_previous?.resolved} /></div>
              <div className="mr-kpi"><span>Open at the end</span><strong>{num(t.open_at_end)}</strong><Change c={t.vs_previous?.open_at_end} invert /></div>
            </div>
            <Trend trend={t.trend} />
            <div className="mr-grid3">
              <div><h3>By category</h3><Bars rows={t.by_category} label="Raised by category" testId="mr-by-category" /></div>
              <div><h3>By priority</h3><Bars rows={t.by_priority} label="Raised by priority" testId="mr-by-priority" /></div>
              <div><h3>By department</h3><Bars rows={t.by_department} label="Raised by department" testId="mr-by-department" /></div>
            </div>
          </Section>

          <Section id="mr-sla" title="SLA" lead={`Tickets raised in the period, each on its SLA profile’s working hours - the same judgement as the SLA board, as at ${fmtDateTime(s.judged_at)}.`}>
            <div className="mr-kpis">
              <div className="mr-kpi"><span>Response met</span><strong>{pct(s.response_rate)}</strong><small>{s.response_met} of {s.tickets}</small></div>
              <div className="mr-kpi"><span>Resolution met</span><strong>{pct(s.resolution_rate)}</strong><small>{s.resolution_met} of {s.tickets}</small></div>
              <div className="mr-kpi"><span>Median response</span><strong>{hrs(s.median_response_hours)}</strong><small>working hours, {s.responded} answered</small></div>
              <div className="mr-kpi"><span>Median resolution</span><strong>{hrs(s.median_resolution_hours)}</strong><small>working hours, {s.resolved} resolved</small></div>
            </div>
            <h3>By SLA profile</h3>
            <Table label="SLA by profile" testId="mr-sla-profiles" empty="No tickets were raised in the period."
              rows={s.by_profile.map((x) => ({ ...x, key: x.name }))}
              columns={[
                { key: 'name', label: 'Profile', render: (r) => <>{r.name}{r.always_open && !/24\s*\/\s*7/.test(r.name) ? <span className="mr-sub"> · 24/7</span> : null}</> },
                { key: 'tickets', label: 'Tickets', num: true },
                { key: 'response_rate', label: 'Response met', num: true, render: (r) => `${pct(r.response_rate)} (${r.response_met})` },
                { key: 'resolution_rate', label: 'Resolution met', num: true, render: (r) => `${pct(r.resolution_rate)} (${r.resolution_met})` },
                { key: 'breached', label: 'Breached', num: true },
                { key: 'median_response_hours', label: 'Median response', num: true, render: (r) => hrs(r.median_response_hours) },
                { key: 'median_resolution_hours', label: 'Median resolution', num: true, render: (r) => hrs(r.median_resolution_hours) }
              ]} />
            <h3>Breaches <span className="mr-count">{s.breaches_total}</span></h3>
            <Table label="SLA breaches" testId="mr-breaches" empty="No ticket raised in the period breached its SLA."
              rows={s.breaches}
              columns={[
                { key: 'ticket_number', label: 'Ticket', render: (r) => (r.id ? <Link to={`/tickets/${r.id}`}>{r.ticket_number || 'Ticket'}</Link> : r.ticket_number) },
                { key: 'subject', label: 'Subject' },
                { key: 'priority', label: 'Priority' },
                { key: 'response', label: 'Response', num: true, render: (r) => <span data-missed={r.response_status === 'missed' ? 'yes' : 'no'}>{hrs(r.response_time_hours)} / {hrs(r.response_sla_hours)}</span> },
                { key: 'resolution', label: 'Resolution', num: true, render: (r) => <span data-missed={r.resolution_status === 'missed' ? 'yes' : 'no'}>{hrs(r.resolution_time_hours)} / {hrs(r.resolution_sla_hours)}</span> },
                { key: 'profile', label: 'Hours' },
                { key: 'state', label: 'Now' }
              ]} />
            {s.breaches_total > s.breaches.length && <p className="mr-sub">The first {s.breaches.length} of {s.breaches_total}; the SLA board has them all.</p>}
          </Section>

          <Section id="mr-hours" title="Contractor hours" lead="Logged by the day worked; approved by when signed off; billed by when invoiced. The Dashboard’s hours figures, for the period.">
            <div className="mr-kpis">
              <div className="mr-kpi"><span>Logged</span><strong>{hrs(h.logged)}</strong></div>
              <div className="mr-kpi"><span>Approved</span><strong>{hrs(h.approved)}</strong></div>
              <div className="mr-kpi"><span>Awaiting sign-off</span><strong>{hrs(h.awaiting)}</strong></div>
              <div className="mr-kpi"><span>Billed</span><strong>{hrs(h.billed)}</strong><small>{money(h.billed_amount)}</small></div>
              <div className="mr-kpi"><span>Ready to bill</span><strong>{hrs(h.ready)}</strong></div>
            </div>
            {(h.missing || []).length > 0 && <p className="as-note" data-tone="info">Not set up on this server yet: {h.missing.join(', ')}.</p>}
            {[['By contractor', h.by_contractor, 'mr-hours-contractor', 'Contractor'], ['By client', h.by_client, 'mr-hours-client', 'Client']].map(([title, rows, id, first]) => (
              <React.Fragment key={id}>
                <h3>{title}</h3>
                <Table label={`Hours ${title.toLowerCase()}`} testId={id} empty="No hours in the period."
                  rows={rows.map((r) => ({ ...r, key: r.id || r.name }))}
                  columns={[
                    { key: 'name', label: first },
                    { key: 'logged', label: 'Logged', num: true, render: (r) => hrs(r.logged) },
                    { key: 'approved', label: 'Approved', num: true, render: (r) => hrs(r.approved) },
                    { key: 'awaiting', label: 'Awaiting', num: true, render: (r) => hrs(r.awaiting) },
                    { key: 'billed', label: 'Billed', num: true, render: (r) => hrs(r.billed) },
                    { key: 'billed_amount', label: 'Billed €', num: true, render: (r) => money(r.billed_amount) },
                    { key: 'ready', label: 'Ready to bill', num: true, render: (r) => hrs(r.ready) }
                  ]} />
              </React.Fragment>
            ))}
          </Section>

          <Section id="mr-assets" title="Devices" lead={a.ready ? `Added and retired in the period. Warranties and devices nobody holds are the register as at ${fmtDate(`${a.as_of}T12:00:00Z`)}.` : null}>
            {!a.ready ? <p className="mr-empty">The asset register is not set up on this server.</p> : (
              <>
                <div className="mr-kpis">
                  <div className="mr-kpi"><span>Added</span><strong>{num(a.added.count)}</strong></div>
                  <div className="mr-kpi"><span>Retired</span><strong>{num(a.retired.count)}</strong></div>
                  <div className="mr-kpi"><span>Warranty ends within 90 days</span><strong>{num(a.warranty_90.count)}</strong></div>
                  <div className="mr-kpi"><span>Nobody holds</span><strong>{num(a.no_owner.count)}</strong>
                    <small><Link to="/assets?holder=none" className="mr-no-print">Open the list</Link></small></div>
                </div>
                <div className="mr-grid2">
                  <div>
                    <h3>Added</h3>
                    <Table label="Devices added" testId="mr-added" empty="None." rows={a.added.items}
                      columns={[{ key: 'asset_tag', label: 'Tag', render: (r) => <Link to={`/assets/${r.id}`}>{r.asset_tag}</Link> },
                        { key: 'category', label: 'Type' }, { key: 'model', label: 'Model' }, { key: 'created_at', label: 'Added', render: (r) => fmtDate(r.created_at) }]} />
                  </div>
                  <div>
                    <h3>Retired</h3>
                    <Table label="Devices retired" testId="mr-retired" empty="None." rows={a.retired.items}
                      columns={[{ key: 'asset_tag', label: 'Tag', render: (r) => <Link to={`/assets/${r.id}`}>{r.asset_tag}</Link> },
                        { key: 'category', label: 'Type' }, { key: 'reason', label: 'Why' }, { key: 'retired_at', label: 'Retired', render: (r) => fmtDate(r.retired_at) }]} />
                  </div>
                </div>
                <h3>Warranties ending in the next 90 days</h3>
                <Table label="Warranties ending" testId="mr-warranty" empty="None in the next 90 days." rows={a.warranty_90.items}
                  columns={[{ key: 'asset_tag', label: 'Tag', render: (r) => <Link to={`/assets/${r.id}`}>{r.asset_tag}</Link> },
                    { key: 'model', label: 'Model' }, { key: 'holder', label: 'Holder', render: (r) => r.holder || '–' },
                    { key: 'warranty_end', label: 'Ends', render: (r) => fmtDate(`${r.warranty_end}T12:00:00Z`) },
                    { key: 'days_left', label: 'Days left', num: true }]} />
                {a.no_owner.count > 0 && (
                  <>
                    <h3>Nobody holds</h3>
                    <Bars rows={a.no_owner.by_category} label="Devices nobody holds, by type" testId="mr-no-owner" />
                  </>
                )}
                <h3>Stock takes finished</h3>
                <Table label="Stock takes" testId="mr-stocktakes" empty="No stock take was finished in the period." rows={a.stocktakes}
                  columns={[{ key: 'place', label: 'Place' }, { key: 'finished_at', label: 'Finished', render: (r) => fmtDate(r.finished_at) },
                    { key: 'expected', label: 'Expected', num: true }, { key: 'found', label: 'Found', num: true },
                    { key: 'misplaced', label: 'Misplaced', num: true }, { key: 'missing', label: 'Missing', num: true }, { key: 'extra', label: 'Extra', num: true }]} />
              </>
            )}
          </Section>

          <Section id="mr-people" title="Joiners and leavers" lead={pe.ready ? 'Whose start or leaving day fell in the period, from their checklists.' : null}>
            {!pe.ready ? <p className="mr-empty">Joiners and leavers are not set up on this server.</p> : (
              <div className="mr-grid2">
                {[['Joiners', pe.joiners, 'mr-joiners'], ['Leavers', pe.leavers, 'mr-leavers']].map(([title, rows, id]) => (
                  <div key={id}>
                    <h3>{title} <span className="mr-count">{rows.length}</span></h3>
                    <Table label={title} testId={id} empty="None." rows={rows}
                      columns={[{ key: 'name', label: 'Name', render: (r) => <Link to={`/people/runs/${r.id}`}>{r.name}</Link> },
                        { key: 'day', label: 'Day', render: (r) => fmtDate(`${r.day}T12:00:00Z`) },
                        { key: 'status', label: 'Checklist', render: (r) => (r.status === 'done' ? 'Done' : `${r.steps_done} of ${r.steps}`) }]} />
                  </div>
                ))}
              </div>
            )}
          </Section>
          <p className="mr-foot">Jarvis · {pack.period.label} · {snapshot && !data.live ? `as sent ${fmtDateTime(snapshot.generated_at)}` : `worked out ${fmtDateTime(pack.generated_at)}`}</p>
        </article>
      )}

      {email && <EmailSettings shownMonth={shownMonth} onClose={() => { setEmail(false); if (location.hash) navigate({ pathname: location.pathname, search: location.search }, { replace: true }); }} />}
    </div>
  );
}
